/** Task status keys aligned with data dictionary (Tasks & Cases list). */
export type TaskStatusKey =
  | 'draft'
  | 'pending'
  | 'pending_assignment'
  | 'assigned'
  | 'in_progress'
  | 'on_hold'
  | 'forwarded'
  | 'reverted'
  | 'escalated'
  | 'sent_for_closure'
  | 'completed'
  | 'rejected'
  | 'pending_knowledge_review'
  | 'published_archived'
  | 'archived';

export type UrgencyLevel = 'High' | 'Medium' | 'Low';

/** Placeholder for null/empty task list fields (data dictionary). */
export const TASK_EMPTY_DISPLAY = '—';

export type UrgencyDisplay = UrgencyLevel | typeof TASK_EMPTY_DISPLAY;

export type TaskListScope =
  | 'active-mine'
  | 'active-linked'
  | 'active-created'
  | 'completed'
  | 'ma-active'
  | 'ma-completed';

const STATUS_LABEL: Record<TaskStatusKey, string> = {
  draft: 'Draft',
  pending: 'Pending',
  pending_assignment: 'Pending Assignment',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  forwarded: 'Forwarded',
  reverted: 'Reverted',
  escalated: 'Escalated',
  sent_for_closure: 'Sent for Closure',
  completed: 'Completed',
  rejected: 'Rejected',
  pending_knowledge_review: 'Pending Knowledge Review',
  published_archived: 'Published & Archived',
  archived: 'Archived',
};

const STATUS_PILL_CLASS: Record<TaskStatusKey, string> = {
  draft: 'p-draft',
  pending: 'p-pend-task',
  pending_assignment: 'p-pend',
  assigned: 'p-assigned',
  in_progress: 'p-ip',
  on_hold: 'p-hold',
  forwarded: 'p-fwd',
  reverted: 'p-rev-task',
  escalated: 'p-esc-task',
  sent_for_closure: 'p-sfc',
  completed: 'p-com',
  rejected: 'p-rej',
  pending_knowledge_review: 'p-pkr',
  published_archived: 'p-arc',
  archived: 'p-arc',
};

export function taskStatusLabel(key: TaskStatusKey): string {
  return STATUS_LABEL[key];
}

export function taskStatusPillClass(key: TaskStatusKey): string {
  return STATUS_PILL_CLASS[key] ?? 'p-aw';
}

export function urgencyTextClass(level: UrgencyLevel | string): string {
  if (level === TASK_EMPTY_DISPLAY) return 't-dash';
  const n = level.toLowerCase();
  if (n === 'high') return 'urg-h';
  if (n === 'medium') return 'urg-m';
  return 'urg-l';
}

export function timeRemainingClass(isOverdue: boolean, hasValue: boolean): string {
  if (!hasValue) return 't-dash';
  return isOverdue ? 't-ov' : 't-ok';
}

/** Map API status_code / status_name to intranet task status key. */
export function taskStatusKeyFromApi(code?: string, name?: string): TaskStatusKey {
  const c = (code ?? '').toUpperCase().replace(/\s+/g, '_');
  const n = (name ?? '').toLowerCase();
  if (c.includes('DRAFT') || n === 'draft') return 'draft';
  if (c.includes('PENDING_ASSIGN') || n.includes('pending assignment')) return 'pending_assignment';
  if (c.includes('ASSIGN') || n === 'assigned') return 'assigned';
  if (c.includes('PENDING_KNOWLEDGE') || n.includes('knowledge review')) return 'pending_knowledge_review';
  if (c.includes('PUBLISHED') || n.includes('published')) return 'published_archived';
  if (c.includes('REJECT') || n === 'rejected') return 'rejected';
  if (c.includes('FORWARD') || n === 'forwarded') return 'forwarded';
  if (c.includes('REVERT') || n === 'reverted') return 'reverted';
  if (c.includes('ESCALAT') || n === 'escalated') return 'escalated';
  if (c.includes('CLOSURE') || n.includes('sent for closure')) return 'sent_for_closure';
  if (c.includes('IN_PROGRESS') || n.includes('in progress')) return 'in_progress';
  if (c.includes('ON_HOLD') || n.includes('on hold') || n.includes('on-hold')) return 'on_hold';
  if (c.includes('PENDING') || n === 'pending') return 'pending';
  if (c.includes('COMPLETE') || n === 'completed') return 'completed';
  if (c.includes('ARCHIV') || n === 'archived') return 'archived';
  return 'pending';
}
